import { useState, useEffect } from 'react'
import { useTheme } from '../context/ThemeContext'
import toast from 'react-hot-toast'

function Settings() {
  const { theme, setTheme } = useTheme()
  const [notifications, setNotifications] = useState(false)
  const [exportData, setExportData] = useState(null)

  // Check notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotifications(Notification.permission === 'granted')
    }
  }, [])

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme)
    toast.success(`Theme set to ${newTheme}`)
  }

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Notifications not supported in this browser')
      return
    }

    try {
      const permission = await Notification.requestPermission()
      setNotifications(permission === 'granted')
      
      if (permission === 'granted') {
        toast.success('Notifications enabled!')
        // Send test notification
        new Notification('Productivity App', {
          body: 'Notifications are now enabled!',
          icon: '/vite.svg',
        })
      } else {
        toast.error('Notification permission denied')
      }
    } catch (error) {
      toast.error('Failed to request notification permission')
    }
  }

  const handleExport = async () => {
    try {
      const response = await fetch('/api/tasks')
      const data = await response.json()
      
      const exportObj = {
        exportedAt: new Date().toISOString(),
        tasks: data.tasks,
      }
      
      const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      
      const a = document.createElement('a')
      a.href = url
      a.download = `productivity-export-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      
      toast.success('Data exported successfully!')
    } catch (error) {
      toast.error('Failed to export data')
    }
  }

  const handleImport = (e) => {
    const file = e.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = async (event) => {
      try {
        const data = JSON.parse(event.target.result)
        
        if (data.tasks && Array.isArray(data.tasks)) {
          // Import tasks one by one
          let imported = 0
          for (const task of data.tasks) {
            try {
              await fetch('/api/tasks', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  title: task.title,
                  description: task.description,
                  priority: task.priority,
                  category: task.category,
                  dueDate: task.dueDate,
                  dueTime: task.dueTime,
                  durationMinutes: task.durationMinutes,
                }),
              })
              imported++
            } catch (err) {
              console.error('Failed to import task:', err)
            }
          }
          
          toast.success(`Imported ${imported} tasks!`)
          window.location.reload()
        } else {
          toast.error('Invalid file format')
        }
      } catch (error) {
        toast.error('Failed to parse file')
      }
    }
    reader.readAsText(file)
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
        Settings
      </h2>

      {/* Appearance */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          🎨 Appearance
        </h3>
        <div className="space-y-4">
          <div>
            <label className="form-label">Theme</label>
            <div className="grid grid-cols-3 gap-3">
              {['light', 'dark', 'system'].map((t) => (
                <button
                  key={t}
                  onClick={() => handleThemeChange(t)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    theme === t
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="text-2xl mb-1">
                    {t === 'light' && '☀️'}
                    {t === 'dark' && '🌙'}
                    {t === 'system' && '💻'}
                  </div>
                  <div className="text-sm font-medium capitalize text-gray-900 dark:text-white">
                    {t}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          🔔 Notifications
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Browser Notifications</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Get notified when tasks are due
            </p>
          </div>
          <button
            onClick={requestNotificationPermission}
            className={`btn ${notifications ? 'btn-secondary' : 'btn-primary'}`}
          >
            {notifications ? 'Enabled' : 'Enable'}
          </button>
        </div>
      </div>

      {/* Data Management */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          💾 Data Management
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Export Data</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Download all your tasks as JSON
              </p>
            </div>
            <button
              onClick={handleExport}
              className="btn btn-secondary"
            >
              Export
            </button>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Import Data</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Import tasks from a JSON file
                </p>
              </div>
              <label className="btn btn-secondary cursor-pointer">
                Import
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* About */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          ℹ️ About
        </h3>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p><strong>Productivity App</strong> v1.0.0</p>
          <p>A complete To-Do List + Daily Planner + Task Reminder system.</p>
          <p>Built with React, Node.js, and SQLite.</p>
        </div>
      </div>
    </div>
  )
}

export default Settings